import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const SolutionBundles = ({ bundles, products, services, onAddBundle, onEditBundle, onDeleteBundle }) => {
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    bundleName: '',
    description: '',
    selectedProducts: [],
    selectedServices: [],
    referenceProjects: '',
    targetMarket: '',
    estimatedValue: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProductToggle = (productId) => {
    setFormData(prev => ({
      ...prev,
      selectedProducts: prev?.selectedProducts?.includes(productId)
        ? prev?.selectedProducts?.filter(id => id !== productId)
        : [...prev?.selectedProducts, productId]
    }));
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      selectedServices: prev?.selectedServices?.includes(serviceId)
        ? prev?.selectedServices?.filter(id => id !== serviceId)
        : [...prev?.selectedServices, serviceId]
    }));
  };

  const handleSubmit = () => {
    if (editingId) {
      onEditBundle(editingId, formData);
      setEditingId(null);
    } else {
      onAddBundle(formData);
    }
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      bundleName: '',
      description: '',
      selectedProducts: [],
      selectedServices: [],
      referenceProjects: '',
      targetMarket: '',
      estimatedValue: ''
    });
    setIsAddFormOpen(false);
  };

  const handleEdit = (bundle) => {
    setFormData(bundle);
    setEditingId(bundle?.id);
    setIsAddFormOpen(true);
  };

  return (
    <div className="space-y-16">
      <div className="flex items-center justify-between gap-12 flex-wrap">
        <div className="flex items-center gap-12">
          <Button
            variant={isAddFormOpen ? "outline" : "default"}
            iconName={isAddFormOpen ? "X" : "Plus"}
            iconPosition="left"
            iconSize={18}
            onClick={() => {
              setIsAddFormOpen(!isAddFormOpen);
              if (isAddFormOpen) resetForm();
            }}
          >
            {isAddFormOpen ? 'Cancel' : 'Create Bundle'}
          </Button>
        </div>
      </div>
      {isAddFormOpen && (
        <div className="bg-card border border-border rounded-lg p-16 md:p-24 shadow-elevation-sm">
          <h3 className="text-lg font-semibold text-foreground mb-16">
            {editingId ? 'Edit Solution Bundle' : 'Create New Solution Bundle'}
          </h3>
          
          <div className="space-y-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              <Input
                label="Bundle Name"
                type="text"
                placeholder="Enter bundle name"
                value={formData?.bundleName}
                onChange={(e) => handleInputChange('bundleName', e?.target?.value)}
                required
              />
              <Input
                label="Target Market"
                type="text"
                placeholder="e.g., Enterprise, SMB"
                value={formData?.targetMarket}
                onChange={(e) => handleInputChange('targetMarket', e?.target?.value)}
              />
            </div>

            <Input
              label="Description"
              type="text"
              placeholder="Enter bundle description"
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div className="bg-muted/30 rounded-lg p-16">
                <h4 className="text-sm font-semibold text-foreground mb-12 flex items-center gap-8">
                  <Icon name="Package" size={16} />
                  Select Products
                </h4>
                <div className="space-y-8 max-h-64 overflow-y-auto">
                  {products?.map((product) => (
                    <Checkbox
                      key={product?.id}
                      label={product?.productName}
                      description={product?.oemBrand}
                      checked={formData?.selectedProducts?.includes(product?.id)}
                      onChange={() => handleProductToggle(product?.id)}
                    />
                  ))}
                </div>
              </div>

              <div className="bg-muted/30 rounded-lg p-16">
                <h4 className="text-sm font-semibold text-foreground mb-12 flex items-center gap-8">
                  <Icon name="Wrench" size={16} />
                  Select Services
                </h4>
                <div className="space-y-8 max-h-64 overflow-y-auto">
                  {services?.map((service) => (
                    <Checkbox
                      key={service?.id}
                      label={service?.serviceName}
                      description={service?.serviceCategory}
                      checked={formData?.selectedServices?.includes(service?.id)}
                      onChange={() => handleServiceToggle(service?.id)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              <Input
                label="Reference Projects"
                type="text"
                placeholder="Enter reference project names"
                value={formData?.referenceProjects}
                onChange={(e) => handleInputChange('referenceProjects', e?.target?.value)}
              />
              <Input
                label="Estimated Value"
                type="text"
                placeholder="e.g., OMR 50,000 - OMR 100,000"
                value={formData?.estimatedValue}
                onChange={(e) => handleInputChange('estimatedValue', e?.target?.value)}
              />
            </div>
          </div>

          <div className="flex items-center gap-12 mt-16">
            <Button variant="default" onClick={handleSubmit}>
              {editingId ? 'Update Bundle' : 'Create Bundle'}
            </Button>
            <Button variant="outline" onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
        {bundles?.map((bundle) => (
          <div key={bundle?.id} className="bg-card border border-border rounded-lg p-16 md:p-24 shadow-elevation-sm">
            <div className="flex items-start justify-between gap-12 mb-16">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-foreground mb-4">{bundle?.bundleName}</h3>
                <p className="text-sm text-muted-foreground">{bundle?.description}</p>
              </div>
              <div className="flex items-center gap-8">
                <Button
                  variant="ghost"
                  size="icon"
                  iconName="Edit"
                  iconSize={16}
                  onClick={() => handleEdit(bundle)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  iconName="Trash2"
                  iconSize={16}
                  onClick={() => onDeleteBundle(bundle?.id)}
                />
              </div>
            </div>

            <div className="space-y-12">
              <div className="flex items-center gap-8 text-sm">
                <Icon name="Target" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">Target Market:</span>
                <span className="text-foreground font-medium">{bundle?.targetMarket}</span>
              </div>

              <div className="flex items-center gap-8 text-sm">
                <Icon name="DollarSign" size={16} className="text-muted-foreground" />
                <span className="text-muted-foreground">Estimated Value:</span>
                <span className="text-foreground font-medium">{bundle?.estimatedValue}</span>
              </div>
            </div>

            <div className="mt-16 pt-16 border-t border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div>
                  <div className="flex items-center gap-8 mb-8">
                    <Icon name="Package" size={14} className="text-primary" />
                    <span className="text-xs font-semibold text-foreground">Products ({bundle?.selectedProducts?.length})</span>
                  </div>
                  <div className="space-y-4">
                    {bundle?.selectedProducts?.slice(0, 3)?.map((productId) => {
                      const product = products?.find(p => p?.id === productId);
                      return product ? (
                        <div key={productId} className="text-xs text-muted-foreground flex items-center gap-4">
                          <Icon name="CheckCircle2" size={12} className="text-success" />
                          {product?.productName}
                        </div>
                      ) : null;
                    })}
                    {bundle?.selectedProducts?.length > 3 && (
                      <div className="text-xs text-primary">+{bundle?.selectedProducts?.length - 3} more</div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-8 mb-8">
                    <Icon name="Wrench" size={14} className="text-primary" />
                    <span className="text-xs font-semibold text-foreground">Services ({bundle?.selectedServices?.length})</span>
                  </div>
                  <div className="space-y-4">
                    {bundle?.selectedServices?.slice(0, 3)?.map((serviceId) => {
                      const service = services?.find(s => s?.id === serviceId);
                      return service ? (
                        <div key={serviceId} className="text-xs text-muted-foreground flex items-center gap-4">
                          <Icon name="CheckCircle2" size={12} className="text-success" />
                          {service?.serviceName}
                        </div>
                      ) : null;
                    })}
                    {bundle?.selectedServices?.length > 3 && (
                      <div className="text-xs text-primary">+{bundle?.selectedServices?.length - 3} more</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {bundle?.referenceProjects && (
              <div className="mt-12 pt-12 border-t border-border">
                <div className="flex items-center gap-8 text-xs">
                  <Icon name="Briefcase" size={14} className="text-muted-foreground" />
                  <span className="text-muted-foreground">Reference Projects:</span>
                  <span className="text-foreground">{bundle?.referenceProjects}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SolutionBundles;